/** Public Google Maps panorama metadata, best effort because this endpoint is undocumented.
 * Protocol reference: streetlevel (MIT, skzk); see THIRD_PARTY_NOTICES.md.
 * We retain panorama IDs/capture months only; imagery stays in Google's viewer.
 */
export type StreetViewCapture = {
  id: string;
  date: string;
  heading?: number;
  /** The panorama Google's search selects for the address today. */
  primary?: boolean;
  /** Signed same-origin proxy path, stamped server-side; imagery stays billed. */
  thumbnail?: string;
};

function at(value: unknown, ...path: number[]): unknown {
  for (const index of path) {
    if (!Array.isArray(value)) return undefined;
    value = value[index];
  }
  return value;
}

export function parseStreetViewCaptures(
  text: string,
  target?: { latitude: number; longitude: number },
): StreetViewCapture[] {
  // The live RPC endpoint returns raw JSON; the retired JSONP endpoint wrapped
  // it in a callback call. Fixtures covering the wrapper remain supported.
  const trimmed = text.trim();
  let inner: string;
  if (trimmed.startsWith('[')) {
    inner = trimmed;
  } else {
    const first = text.indexOf('(');
    const last = text.lastIndexOf(')');
    if (first < 0 || last <= first) throw new Error('Invalid panorama metadata');
    inner = text.slice(first + 1, last);
  }
  const response: unknown = JSON.parse(`[${inner}]`);
  if (at(response, 0, 0, 0) === 5) return [];
  if (at(response, 0, 0, 0) !== 0) throw new Error('Panorama metadata unavailable');
  const message = at(response, 0, 1);
  const captures = new Map<string, StreetViewCapture>();
  const add = (id: unknown, date: unknown, location: unknown) => {
    const year = at(date, 0),
      month = at(date, 1);
    if (
      typeof id !== 'string' ||
      !/^[A-Za-z0-9_-]{10,100}$/.test(id) ||
      typeof year !== 'number' ||
      typeof month !== 'number' ||
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      year < 2007 ||
      year > new Date().getUTCFullYear() ||
      month < 1 ||
      month > 12
    )
      return;
    const lat = at(location, 2),
      lon = at(location, 3);
    const heading =
      target &&
      typeof lat === 'number' &&
      typeof lon === 'number' &&
      Number.isFinite(lat) &&
      Number.isFinite(lon)
        ? ((Math.atan2(
            (target.longitude - lon) * Math.cos((target.latitude * Math.PI) / 180),
            target.latitude - lat,
          ) *
            180) /
            Math.PI +
            360) %
          360
        : undefined;
    captures.set(id, {
      heading,
      id,
      date: `${year}-${String(month).padStart(2, '0')}-01`,
    });
  };
  add(at(message, 1, 1), at(message, 6, 7), at(message, 5, 0, 1, 0));
  const dates = at(message, 5, 0, 8);
  if (Array.isArray(dates))
    for (const entry of dates) {
      const index = at(entry, 0);
      if (typeof index === 'number' && Number.isInteger(index) && index >= 0)
        add(
          at(message, 5, 0, 3, 0, index, 0, 1),
          at(entry, 1),
          at(message, 5, 0, 3, 0, index, 2, 0),
        );
    }
  const primaryId = at(message, 1, 1);
  const primary = typeof primaryId === 'string' ? captures.get(primaryId) : undefined;
  if (primary) primary.primary = true;
  return [...captures.values()].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 100);
}

/** Raw contributor IDs need a type-10 envelope in the Maps APIs. The metadata
 * RPC returns the inner ID, unlike the public JavaScript/Static APIs. Keep the
 * source ID intact; adapt only outbound URLs. See streetlevel's util.py.
 */
function contributorPanoramaId(id: string): string | null {
  try {
    const decoded = atob(id.replaceAll('-', '+').replaceAll('_', '/').replaceAll('.', '='));
    if (
      decoded.charCodeAt(0) === 8 &&
      decoded.charCodeAt(1) === 10 &&
      decoded.charCodeAt(2) === 18 &&
      decoded.charCodeAt(3) === decoded.length - 4 &&
      /^[A-Za-z0-9_-]{10,100}$/.test(decoded.slice(4))
    )
      return decoded.slice(4);
  } catch {
    // A raw ID need not itself be valid base64.
  }
  return /^[A-Za-z0-9_-]{10,100}$/.test(id) && (id.startsWith('CIHM0og') || id.length > 22)
    ? id
    : null;
}

function panoramaApiId(id: string) {
  const contributor = contributorPanoramaId(id);
  if (!contributor) return id;
  return btoa(String.fromCharCode(8, 10, 18, contributor.length) + contributor)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '');
}

export function panoramaUrl(id: string) {
  const contributor = contributorPanoramaId(id);
  if (contributor)
    return `https://www.google.com/maps/@0,0,3a,90y,0h,90t/data=!3m4!1e1!3m2!1s${encodeURIComponent(contributor)}!2e10`;
  return `https://www.google.com/maps/@?api=1&map_action=pano&pano=${encodeURIComponent(id)}`;
}

/** Canonical whole-degree heading shared by proxy paths, signatures and upstream URLs. */
export function panoramaHeading(heading = 0) {
  return Number.isFinite(heading) ? Math.round(((heading % 360) + 360) % 360) % 360 : 0;
}

const thumbnailSignatureScope = 'parcel-watch:street-view-thumbnail:v1';

/** HMAC the proxy route verifies before spending a billed upstream request. */
export async function panoramaThumbnailSignature(id: string, heading = 0, secret: string) {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const data = new TextEncoder().encode(
    `${thumbnailSignatureScope}:${id}:${panoramaHeading(heading)}`,
  );
  return [...new Uint8Array(await crypto.subtle.sign('HMAC', key, data))]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

/** Same-origin preview path. The app validates, proxies and caches Google image bytes. */
export function panoramaThumbnailPath(id: string, heading = 0, signature?: string) {
  const parameters = new URLSearchParams({ id, heading: String(panoramaHeading(heading)) });
  if (signature) parameters.set('sig', signature);
  return `/api/parcel-watch/street-view/thumbnail?${parameters}`;
}

/** Official Street View Static API image. Billed per request, so callers must proxy and cache. */
export function panoramaStaticUrl(id: string, heading = 0, apiKey: string, size = '640x360') {
  return `https://maps.googleapis.com/maps/api/streetview?${new URLSearchParams({
    size,
    pano: panoramaApiId(id),
    heading: String(panoramaHeading(heading)),
    pitch: '0',
    fov: '100',
    return_error_code: 'true',
    key: apiKey,
  })}`;
}
